import { formatAccessTerm } from '@/features/billing/access-term';
import {
  ISSUE_REPORT_OFFER_STATE,
  ISSUE_REPORT_OFFER_STRINGS,
  OFFER_TOKEN,
} from '@/lib/constants/issue-report';
import type { IssueReportOffer } from '@/lib/types/billing';
import { formatCents } from './format.utils';

/** A well-formed placeholder, `{{name}}`, with optional spaces inside the braces. */
const TOKEN_PATTERN = /\{\{\s*(\w+)\s*\}\}/g;
/** Anything brace-shaped still left after the well-formed ones: `{{x}`, `{x}}`, `{{x`, a lone `}}`. */
const BRACE_GROUP_PATTERN = /\{+[^{}]*\}+/g;
const OPEN_FRAGMENT_PATTERN = /\{+\s*\w*/g;
const STRAY_BRACE_PATTERN = /[{}]/g;

/** The `.00` of a whole-dollar amount. */
const WHOLE_DOLLAR_SUFFIX = /\.00$/;

/** True when the text holds, or looks like it was meant to hold, a placeholder. */
function hasPlaceholder(text: string): boolean {
  return text.includes('{{');
}

/**
 * The offer price as running copy reads it: `$250` for whole dollars and
 * `$249.50` otherwise. Billing screens keep `formatCents`'s `.00` because they
 * list prices in a column; a price inside a sentence reads better without it.
 */
function formatOfferPrice(priceCents: number): string {
  return formatCents(priceCents)?.replace(WHOLE_DOLLAR_SUFFIX, '') ?? '';
}

/** What one placeholder name becomes for the offer in hand. Unknown names become nothing. */
function tokenValue(name: string, offer: IssueReportOffer | undefined): string {
  if (!offer) return '';
  if (offer.state === ISSUE_REPORT_OFFER_STATE.COMING_SOON) {
    return name === OFFER_TOKEN.PRICE ? ISSUE_REPORT_OFFER_STRINGS.COMING_SOON : '';
  }
  if (name === OFFER_TOKEN.PRICE) return formatOfferPrice(offer.priceCents);
  if (name === OFFER_TOKEN.ACCESS_LENGTH) {
    return (
      formatAccessTerm({
        accessTermDays: offer.accessTerm.days,
        accessTermMonths: offer.accessTerm.months,
      }) ?? ''
    );
  }
  return '';
}

/**
 * Whether any of these CMS strings carries an offer placeholder, so a caller
 * only asks for the offer when it has something to fill (FR-202).
 */
export function usesOfferTokens(...texts: (string | undefined)[]): boolean {
  return texts.some((text) => text !== undefined && hasPlaceholder(text));
}

/**
 * Fills the CMS placeholders `{{issueReportPrice}}` and
 * `{{issueReportAccessLength}}` from the public offer (FR-202, A-230).
 *
 * - `available`: the formatted price and the access length in the plan's own
 *   unit ("7 days", "3 months"). A length the plan does not carry is removed.
 * - `coming_soon`: the price becomes "Coming Soon"; the length is removed.
 * - `undefined` (still loading, or the request failed): every placeholder is
 *   removed, so the page shows its descriptive text and never a stale number.
 *
 * Unknown or malformed placeholders are removed too, so a raw `{{...}}` never
 * reaches the screen. Text without a placeholder is returned untouched.
 *
 * @param text  the CMS string
 * @param offer the offer, or undefined while it is not known
 */
export function fillOfferTokens(text: string, offer: IssueReportOffer | undefined): string {
  if (!hasPlaceholder(text) && !/[{}]/.test(text)) return text;
  return text
    .replace(TOKEN_PATTERN, (_match, name: string) => tokenValue(name, offer))
    .replace(BRACE_GROUP_PATTERN, '')
    .replace(OPEN_FRAGMENT_PATTERN, '')
    .replace(STRAY_BRACE_PATTERN, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();
}

/**
 * A price line (a whole line that is the price, e.g. "One-time fee of
 * {{issueReportPrice}}"). Unlike running text, it cannot be left half-filled:
 *
 * - no placeholder: returned as written (the Seed Financing band's line);
 * - offer not known: dropped, so there is no price line rather than a dangling
 *   "One-time fee of" (FR-202);
 * - `coming_soon` with a price placeholder: the whole line reads "Coming Soon",
 *   which is the price position showing that state (FR-202);
 * - otherwise filled as for `fillOfferTokens`, and dropped if nothing is left.
 *
 * @returns the line to show, or undefined for none
 */
export function fillOfferLine(
  text: string | undefined,
  offer: IssueReportOffer | undefined,
): string | undefined {
  if (!text) return undefined;
  if (!hasPlaceholder(text)) return text;
  if (!offer) return undefined;
  if (offer.state === ISSUE_REPORT_OFFER_STATE.COMING_SOON && mentionsPrice(text)) {
    return ISSUE_REPORT_OFFER_STRINGS.COMING_SOON;
  }
  return fillOfferTokens(text, offer) || undefined;
}

function mentionsPrice(text: string): boolean {
  return new RegExp(`\\{\\{\\s*${OFFER_TOKEN.PRICE}\\s*\\}\\}`).test(text);
}
